import React, { useState } from 'react';
import { format } from 'date-fns';
import PostGameAnalysis from './PostGameAnalysis';
import GameRecap from './GameRecap';
import MatchupPreviewModal from './matchup/MatchupPreviewModal';

const InfoPill = ({ label, value, highlight = false }) => (
  <div className={`flex flex-col gap-0.5 px-3 py-2 border bg-[rgba(0,0,0,0.08)] min-w-0 ${highlight ? 'border-[#d8ceb3]' : 'border-[#658d71]'}`}>
    <span className="text-[8px] sm:text-[9px] tracking-widest text-[#a9c9b1] uppercase font-bold whitespace-nowrap">{label}</span>
    <span className={`text-[10px] sm:text-xs font-black tracking-wide uppercase leading-tight break-words ${highlight ? 'text-[#d8ceb3]' : 'text-white'}`}>
      {value || '--'}
    </span>
  </div>
);

const ActionButton = ({ onClick, children, primary = false }) => (
  <button
    onClick={onClick}
    className={`flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest px-4 py-2 transition-all duration-200 border whitespace-nowrap ${
      primary
        ? 'bg-[#386646] border-[#8aa890] hover:border-[#d8ceb3] text-[#d8ceb3] hover:text-white'
        : 'bg-[rgba(0,0,0,0.15)] border-[#658d71] hover:border-[#a9c9b1] text-[#a9c9b1] hover:text-white'
    }`}
  >
    {children}
  </button>
);

const GameSummary = ({ game, lastUpdated }) => {
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [showMatchup, setShowMatchup] = useState(false);

  const isFinal = game?.status?.abstractGameState === 'Final';
  const isLive = game?.status?.abstractGameState === 'Live';
  const isPreview = game?.status?.abstractGameState === 'Preview';

  const status = game?.status?.detailedState || '--';
  const venue = game?.venue?.name || '--';
  const firstPitchTime = game ? format(new Date(game.gameDate), 'h:mm a') : '--';
  const updatedStr = lastUpdated ? format(lastUpdated, 'h:mm:ss a') : '--';

  const winner = game?.decisions?.winner?.fullName;
  const loser = game?.decisions?.loser?.fullName;

  let record = '--';
  if (game) {
    const away = game.teams.away.team.abbreviation || 'AWY';
    const home = game.teams.home.team.abbreviation || 'HME';
    const awayRec = game.teams.away.leagueRecord;
    const homeRec = game.teams.home.leagueRecord;
    if (awayRec && homeRec)
      record = `${away} ${awayRec.wins}-${awayRec.losses}  ·  ${home} ${homeRec.wins}-${homeRec.losses}`;
  }

  const hasButtons = game?.gamePk;
  const showMatchupBtn = isPreview || (!isFinal && !isLive);
  const showRecapBtn = isFinal;
  const showAnalysisBtn = isFinal || isLive;

  return (
    <>
      <div className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.05)]">
        {/* Top row: info pills + buttons side by side on desktop */}
        <div className="flex flex-col sm:flex-row sm:items-stretch gap-0 divide-y sm:divide-y-0 sm:divide-x divide-[rgba(101,141,113,0.3)]">

          {/* Info pills section */}
          <div className="flex-1 p-3 sm:p-4 flex flex-wrap gap-2">
            <InfoPill label="Status" value={status} />
            <InfoPill label="First Pitch" value={firstPitchTime} />
            <InfoPill label="Venue" value={venue} />
            {isFinal ? (
              <>
                <InfoPill label="Winner" value={winner} highlight />
                <InfoPill label="Loser" value={loser} />
              </>
            ) : (
              <InfoPill label="Records" value={record} />
            )}
            <InfoPill label="Updated" value={updatedStr} />
          </div>

          {/* Action buttons — vertical stack on right side on desktop, horizontal on mobile */}
          {hasButtons && (
            <div className="flex flex-row sm:flex-col justify-end sm:justify-center gap-2 p-3 sm:p-4 shrink-0">
              {showMatchupBtn && (
                <ActionButton onClick={() => setShowMatchup(true)}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#a9c9b1] shrink-0" />
                  <span className="hidden sm:inline">Matchup Preview</span>
                  <span className="sm:hidden">Preview</span>
                </ActionButton>
              )}
              {showRecapBtn && (
                <ActionButton onClick={() => setShowRecap(true)}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d8ceb3] shrink-0" />
                  <span className="hidden sm:inline">Game Recap</span>
                  <span className="sm:hidden">Recap</span>
                </ActionButton>
              )}
              {showAnalysisBtn && (
                <ActionButton onClick={() => setShowAnalysis(true)} primary>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c4333e] shrink-0" />
                  <span className="hidden sm:inline">{isFinal ? 'Post-Game Analysis' : 'Live Analysis'}</span>
                  <span className="sm:hidden">{isFinal ? 'Analysis' : 'Live'}</span>
                </ActionButton>
              )}
            </div>
          )}
        </div>
      </div>

      {showMatchup && <MatchupPreviewModal game={game} onClose={() => setShowMatchup(false)} />}
      {showAnalysis && <PostGameAnalysis game={game} onClose={() => setShowAnalysis(false)} />}
      {showRecap && <GameRecap game={game} onClose={() => setShowRecap(false)} />}
    </>
  );
};

export default GameSummary;